import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  ArrowRight,
  Calendar,
  CheckCircle,
  ChartLine,
  ClipboardText,
  CurrencyDollar,
  GearSix,
  Lightning,
  Lock,
  Megaphone,
  Target,
  UsersThree
} from '@phosphor-icons/react';

const MCPPage = () => {
  const navigate = useNavigate();

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Integrations - Connect Your Workers to 1000+ Apps | Man & Machine",
    "description": "Connect your AI workers to email, calendar, CRM, and business tools so they can take action automatically.",
    "publisher": {
      "@type": "Organization",
      "name": "Man & Machine",
      "url": "https://manandmachine.ai"
    }
  };

  return (
    <div className="min-h-screen bg-[#161B26] text-white overflow-x-hidden">
      <SEO
        title="Integrations - Connect Your Workers to 1000+ Apps | Man & Machine"
        description="Connect your AI workers to email, calendar, CRM, and business tools so they can take action automatically. Powered by direct integrations and MCP."
        keywords="AI integrations, business automation, CRM integration, email automation, calendar automation, MCP, workflow automation"
        canonicalUrl="https://manandmachine.ai/mcp"
        structuredData={structuredData}
      />
      <LandingHeader />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,229,192,0.06)] via-transparent to-[rgba(0,229,192,0.03)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] sm:w-[800px] h-[300px] sm:h-[400px] bg-[rgba(0,229,192,0.08)] blur-[120px] rounded-full" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
              <Lightning size={14} className="text-[#00E5C0]" />
              <span className="text-xs text-[#A0AEC0]">Integrations</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Connect Your Workers to{' '}
              <span className="text-[#00E5C0]">1000+ Apps</span>
            </h1>

            <p className="text-base sm:text-lg text-[#A0AEC0] mb-3 max-w-2xl mx-auto leading-relaxed">
              Connect your AI workers to your email, calendar, CRM, and business tools - so they can take action automatically.
            </p>

            <p className="text-sm text-[#7D8896] mb-8">
              Powered by direct integrations and MCP for advanced data connections.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
                data-testid="integrations-hero-cta"
              >
                Get Started Free
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => document.getElementById('connect-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all"
                data-testid="integrations-explore"
              >
                Explore Integrations
              </button>
            </div>

            {/* Optional positioning line */}
            <p className="text-sm text-[#A0AEC0] italic">
              Your workers are only as powerful as what they can connect to - now they can connect to everything.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ CONNECT TO EVERYTHING ═══ */}
      <section id="connect-section" className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Connect to Everything Your{' '}
              <span className="text-[#00E5C0]">Business Uses</span>
            </h2>
            <p className="text-[#A0AEC0] max-w-2xl mx-auto">
              From everyday tools to advanced systems, your workers can access and act across your entire stack.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Megaphone,
                title: 'Popular Apps',
                desc: 'Connect tools like email, calendar, CRM, and messaging platforms.',
                color: '#00E5C0'
              },
              {
                icon: ClipboardText,
                title: 'Business Systems',
                desc: 'Integrate with job management, accounting, and internal tools.',
                color: '#EC4899'
              },
              {
                icon: GearSix,
                title: 'Advanced Data (MCP)',
                desc: 'Connect to databases, APIs, and custom systems using MCP when needed.',
                color: '#F59E0B'
              }
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.25)] transition-colors"
                data-testid={`connect-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${card.color}15` }}
                >
                  <card.icon size={24} style={{ color: card.color }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-[#A0AEC0] text-sm">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 1000+ APPS ═══ */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Connect to <span className="text-[#00E5C0]">1000+ Apps</span>
            </h2>
            <p className="text-[#A0AEC0] max-w-2xl mx-auto">
              Your workers can interact with the tools you already use - no custom setup required.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Megaphone, title: 'Communication', desc: 'Email, SMS, messaging, and notifications', color: '#00E5C0' },
              { icon: Calendar, title: 'Scheduling', desc: 'Calendars, booking systems, appointments', color: '#EC4899' },
              { icon: Target, title: 'CRM & Sales', desc: 'Customer data, pipelines, lead management', color: '#F59E0B' },
              { icon: ClipboardText, title: 'Operations', desc: 'Job management, internal workflows', color: '#8B5CF6' },
              { icon: CurrencyDollar, title: 'Finance', desc: 'Invoices, payments, accounting tools', color: '#06B6D4' },
              { icon: GearSix, title: 'Data & APIs', desc: 'Databases, APIs, and custom systems (via MCP)', color: '#A0AEC0' }
            ].map((cat, i) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.08 }}
                className="glass rounded-2xl p-5 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.2)] transition-colors"
                data-testid={`category-${i}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <cat.icon size={20} style={{ color: cat.color }} />
                  <h3 className="text-white font-semibold">{cat.title}</h3>
                </div>
                <p className="text-sm text-[#A0AEC0]">{cat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHY THIS MATTERS ═══ */}
      <section className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center"
          >
            Why This <span className="text-[#00E5C0]">Matters</span>
          </motion.h2>

          <div className="space-y-4">
            {[
              {
                icon: Lightning,
                title: 'Your workers can take real action',
                desc: 'Send emails, book jobs, update systems - not just generate text'
              },
              {
                icon: Lock,
                title: 'Everything stays connected',
                desc: 'No switching between tools or manual updates'
              },
              {
                icon: GearSix,
                title: 'Works with your existing stack',
                desc: 'No need to replace the tools you already use'
              },
              {
                icon: ChartLine,
                title: 'Scales with your business',
                desc: 'Start simple, expand into more systems over time'
              }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
                className="glass rounded-xl p-5 border border-[rgba(255,255,255,0.05)] flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-[rgba(0,229,192,0.1)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <item.icon size={20} className="text-[#00E5C0]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-[#A0AEC0]">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-2xl sm:text-3xl font-bold text-white mb-12 text-center"
          >
            How It <span className="text-[#00E5C0]">Works</span>
          </motion.h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: '1', title: 'Connect your tools', desc: 'Email, calendar, CRM, and more', color: '#00E5C0' },
              { num: '2', title: 'Assign them to workers', desc: 'Choose what each worker can access', color: '#EC4899' },
              { num: '3', title: 'Workers take action', desc: 'They respond, update, and automate tasks', color: '#F59E0B' },
              { num: '4', title: 'Everything stays in sync', desc: 'Your systems update automatically', color: '#8B5CF6' }
            ].map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] text-center"
                data-testid={`step-${step.num}`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${step.color}15` }}
                >
                  <span className="font-bold text-lg" style={{ color: step.color }}>{step.num}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-[#A0AEC0] text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHAT YOUR WORKERS CAN DO ═══ */}
      <section className="py-20 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center"
          >
            What Your Workers <span className="text-[#00E5C0]">Can Do</span>
          </motion.h2>

          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: Target, title: 'Handle Leads', desc: 'Respond instantly and update your CRM', color: '#00E5C0' },
              { icon: Calendar, title: 'Manage Bookings', desc: 'Schedule jobs and update calendars', color: '#EC4899' },
              { icon: UsersThree, title: 'Send Follow-Ups', desc: 'Automatically message customers', color: '#F59E0B' },
              { icon: ClipboardText, title: 'Update Systems', desc: 'Log activity, update records, trigger workflows', color: '#8B5CF6' }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.25)] transition-colors"
                data-testid={`action-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <item.icon size={20} style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-sm text-[#A0AEC0]">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass rounded-2xl p-8 sm:p-10 text-center border border-[rgba(0,229,192,0.15)]"
            data-testid="final-cta-section"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Connect Your Business.{' '}
              <span className="text-[#00E5C0]">Let Workers Do the Rest.</span>
            </h2>
            <p className="text-[#A0AEC0] mb-8 max-w-xl mx-auto">
              Set up integrations in minutes and start automating real work across your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
                data-testid="cta-get-started"
              >
                Get Started Free
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => document.getElementById('connect-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all"
              >
                See How It Works
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MCPPage;
